<template>
  <content-popup
    v-click-outside:#app.stop.prevent.capture="handleClose"
    class="resource-prerequisite-view-popup"
    size="small"
    :title="title"
    :visible="visible"
    :show-cancel-button="false"
    :show-save-button="false"
    :show-close-button="true"
    :reference-element="referenceElement"
    :placement="placement"
    @on-close="handleClose"
  >
    <template #header-icon>
      <div class="popup-icon">
        <svg-icon :name="lockName" kind="indicative" />
      </div>
    </template>
    <template #content>
      <div class="subtitle">
        {{ subtitle }}
      </div>

      <ul class="resource-prerequisite-list">
        <template v-for="(prerequisite, index) in existingPrerequisites" :key="index">
          <li
            class="resource-prerequisite-item"
            :class="{
              'is-section-prerequisite': showSectionIcon(prerequisite),
              'no-activity-title': !showActivityTitle(prerequisite),
              'has-separator': showOperator(prerequisite, index),
              'is-game-condition': isGameCondition(prerequisite.criterion),
              'is-game-choice': isGameChoiceType(prerequisite),
              clickable: isGameAwardConditionEnabled(prerequisite),
              'no-criterion-text': isSectionUnlock,
              'is-game-disabled': isGameConditionAndGameDisabled(prerequisite.criterion),
            }"
          >
            <cv-link
              @click="
                isGameAwardConditionEnabled(prerequisite) ? handleOpenResource(prerequisite) : null
              "
            >
              <div class="wrapper">
                <template v-if="!hasAccess(prerequisite)">
                  <span v-if="showSectionIcon(prerequisite)" class="icon">
                    <svg-icon :name="getSectionIcon(prerequisite)" kind="indicative" />
                    <span v-if="!hideSectionActivityNumber" class="page-label">
                      {{ sectionLabel(prerequisite.sectionPageNo) }}
                    </span>
                  </span>
                  <cv-tooltip
                    v-if="conditionTip(prerequisite)"
                    alignment="start"
                    direction="bottom"
                    :tip="conditionTip(prerequisite)"
                  >
                    <svg-icon :name="iconName(prerequisite)" kind="indicative" class="disabled" />
                  </cv-tooltip>
                  <svg-icon
                    v-else
                    :name="iconName(prerequisite)"
                    kind="indicative"
                    class="disabled"
                  />
                </template>
                <template v-else>
                  <span v-if="showSectionIcon(prerequisite)" class="icon">
                    <svg-icon
                      v-if="hasAccess(prerequisite)"
                      :name="getSectionIcon(prerequisite)"
                      kind="indicative"
                    />
                    <span v-if="!hideSectionActivityNumber" class="page-label">
                      {{ sectionLabel(prerequisite.sectionPageNo) }}
                    </span>
                  </span>
                  <span v-else-if="showIconOtherThanSectionIcon(prerequisite)" class="icon">
                    <svg-icon
                      v-if="hasAccess(prerequisite)"
                      :name="iconName(prerequisite)"
                      kind="indicative"
                    />
                  </span>
                  <span v-if="showActivityTitle(prerequisite)" class="icon">
                    <svg-icon
                      v-if="hasAccess(prerequisite)"
                      :name="iconName(prerequisite)"
                      kind="indicative"
                    />
                    <span class="page-label"> {{ prerequisite.activityPageNo }} </span>
                  </span>
                  <span v-if="isGameChoiceType(prerequisite)" class="icon">
                    <svg-icon
                      v-if="hasAccess(prerequisite)"
                      :name="iconName(prerequisite)"
                      kind="indicative"
                    />
                  </span>
                </template>
                <!-- Page Title -->
                <span class="page-title">{{ titleLabel(prerequisite) }}</span>

                <div v-if="isGameChoiceType(prerequisite)">
                  <!-- Choice History -->
                  <div v-if="showChoiceHistory(prerequisite.choiceGameAwardId)" class="choice-icon">
                    <svg-icon
                      class="icon"
                      :name="chosenChoiceHistoryIcon(prerequisite.gameChoice)"
                      kind="indicative"
                    />
                    <span class="criterion-text">{{
                      chosenChoiceHistory(prerequisite.gameChoice)
                    }}</span>
                  </div>
                  <div v-else>
                    <!-- Choice Icon -->
                    <div class="choice-icon">
                      <svg-icon
                        :name="choiceIcon(prerequisite)"
                        kind="indicative"
                        :class="hasAccess(prerequisite) ? '' : 'disabled'"
                      />
                      <!-- Criterion Text -->
                      <span class="criterion-text">
                        {{ criterionText(prerequisite) }}
                      </span>
                    </div>
                  </div>
                </div>
                <div v-else-if="!isSectionUnlock">
                  <!-- Criterion Text -->
                  <span class="criterion-text">
                    {{ criterionText(prerequisite) }}
                  </span>
                </div>
              </div>
            </cv-link>
          </li>
          <li
            v-if="showOperator(prerequisite, index)"
            :key="index"
            class="resource-prerequisite-separator"
          >
            {{ operatorLabel }}
          </li>
        </template>
      </ul>
    </template>
  </content-popup>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";
import StringUtils from "@utils/string.utils";
import { RULE_CONDITION_TYPE } from "@modules/editor/service/editor.constant";
import { PREREQUISITE_OPERATOR } from "@modules/rule-condition/service/rule.condition.constant";
import { EVENT } from "@plugins/course-lesson-plan/course.constant";
import {
  CONDITION_TYPE,
  GAME_AWARD_TYPE,
  GAME_CHOICE_TYPE,
} from "@modules/editor/service/gamification.constant";
import { isEmpty, isNull, notDefined } from "@utils/var.check";
import { clickOutsideDirective } from "@utils/directives";
import ContentPopup from "@plugins/common/components/ContentPopup.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { LESSON_ACTIVITY_TYPE } from "@plugins/common/constant/resource.constant";
import {
  GameAward,
  ChoiceHistory,
  UserGameData,
  PrerequisiteItem,
  OperatorItem,
} from "@plugins/course-lesson-plan/types.ts";

const vClickOutside = clickOutsideDirective["click-outside"];

const props = withDefaults(
  defineProps<{
    currentSectionId?: number;
    currentActivityId?: number;
    isActivityQuiz?: boolean;
    isSectionPrerequisite?: boolean;
    isSectionUnlock?: boolean;
    isAchievementCondition?: boolean;
    isGameStoryCondition?: boolean;
    isGameAvatarCondition?: boolean;
    isDigitalBadgeCondition?: boolean;
    isGameEnabled?: boolean;
    gameAward?: { choiceType?: number };
    choice?: Record<string, unknown>;
    existingPrerequisites?: PrerequisiteItem[];
    existingOperators?: OperatorItem[];
    referenceElement?: HTMLElement | null;
    placement?: string;
    hideSectionActivityNumber?: boolean;
    inGameEdit?: boolean;
    userGameData?: UserGameData;
    allOtherResponseBranchIdList?: number[];
    choiceListById?: Record<number, ChoiceHistory>;
    gameAwards?: GameAward[];
  }>(),
  {
    currentSectionId: undefined,
    currentActivityId: undefined,
    isActivityQuiz: false,
    isSectionPrerequisite: false,
    isSectionUnlock: false,
    isAchievementCondition: false,
    isGameStoryCondition: false,
    isGameAvatarCondition: false,
    isDigitalBadgeCondition: false,
    isGameEnabled: false,
    gameAward: () => ({}),
    choice: () => ({}),
    existingPrerequisites: () => [],
    existingOperators: () => [],
    referenceElement: null,
    placement: "bottom",
    hideSectionActivityNumber: false,
    inGameEdit: false,
    userGameData: () => ({}) as UserGameData,
    allOtherResponseBranchIdList: () => [],
    choiceListById: () => ({}),
    gameAwards: () => [],
  },
);

const emit = defineEmits<{
  (e: "close"): void;
  (e: "event", payload: { type: string; data: Record<string, unknown> }): void;
  (e: "gameEvent", payload: { data: Record<string, unknown> }): void;
}>();

const { t } = useI18n();
const store = useStore();

const visible = ref(false);

const isStudent = computed<boolean>(() => store.getters["auth/isStudent"]);

const lockName = computed<string>(() => (props.isSectionUnlock ? "Unlock24" : "Lock24"));

const isGameAwardConditionEnabled = (prerequisite: PrerequisiteItem) =>
  (!props.inGameEdit || isLevelUp(prerequisite.criterion)) && hasAccess(prerequisite);

const conditionTip = (prerequisite: PrerequisiteItem) => {
  if (isQuizMarkAward(prerequisite.criterion))
    return t("gamification_condition_popup_quiz_not_available_tooltip") as string;
  if (isGameAwardOrGameChoiceStory(prerequisite))
    return t("gamification_condition_popup_game_story_not_awarded_tooltip") as string;
  if (isGameAwardOrGameChoiceAchievement(prerequisite))
    return t("gamification_condition_popup_achievement_not_awarded_tooltip") as string;
  if (isGameAwardOrGameChoiceAvatar(prerequisite))
    return t("gamification_condition_popup_avatar_not_awarded_tooltip") as string;
  if (isGameAwardOrGameChoiceBadge(prerequisite))
    return t("gamification_condition_popup_digital_badge_not_awarded_tooltip") as string;
  return "";
};

const title = computed<string>(() => {
  const conditionLabelMap: Record<string, string> = {
    isGameStoryCondition: "gamification_game_story_popup_header_label",
    isAchievementCondition: "gamification_achievement_popup_header_label",
    isGameAvatarCondition: "gamification_game_avatar_popup_header_label",
    isDigitalBadgeCondition: "gamification_digital_badge_popup_header_label",
    isSectionPrerequisite: "prerequisite_content_popup_section_title",
    isSectionUnlock: "section_end_content_popup_title",
    isActivityQuiz: "prerequisite_content_popup_quiz_title",
    defaultCondition: "prerequisite_content_popup_activity_title",
  };

  const k = (Object.keys(conditionLabelMap) as (keyof typeof conditionLabelMap)[]).find(
    (key) => (props as Record<string, unknown>)[key],
  );

  return k
    ? (t(conditionLabelMap[k]) as string)
    : (t(conditionLabelMap.defaultCondition) as string);
});

const subtitle = computed<string>(() => {
  const conditionLabelMap: Record<string, string> = {
    isGameStoryCondition: "gamification_game_story_popup_subtitle_label",
    isAchievementCondition: "gamification_achievement_popup_subtitle_label",
    isGameAvatarCondition: "gamification_game_avatar_popup_subtitle_label",
    isDigitalBadgeCondition: "gamification_digital_badge_popup_subtitle_label",
    isSectionPrerequisite: "prerequisite_content_popup_section_subtitle",
    isSectionUnlock: "section_end_content_popup_subtitle",
    isActivityQuiz: "prerequisite_content_popup_quiz_subtitle",
    defaultCondition: "prerequisite_content_popup_activity_subtitle",
  };

  const k = (Object.keys(conditionLabelMap) as (keyof typeof conditionLabelMap)[]).find(
    (key) => (props as Record<string, unknown>)[key],
  );

  return k
    ? (t(conditionLabelMap[k]) as string)
    : (t(conditionLabelMap.defaultCondition) as string);
});

const sectionLabel = (pageNo?: number) =>
  pageNo ? StringUtils.getAlphabeticalLetter(pageNo - 1) : "";

const operatorLabel = computed<string>(() => PREREQUISITE_OPERATOR.OR);

const showChoiceHistory = (choiceGameAwardId?: string | number) => {
  if (isEmpty(props.choiceListById)) return false;
  const gameAwardId = parseInt(String(choiceGameAwardId));
  const gameAward = props.gameAwards.find((ga) => ga.id === gameAwardId);
  if (isEmpty(gameAward) || isEmpty(gameAward?.gameChoiceList)) return false;
  const c = Object.values(props.choiceListById).find((ch) => ch.gameAwardId === gameAwardId);
  return !isEmpty(c);
};

const showOperator = (prerequisite: PrerequisiteItem, index: number) => {
  if (isNull(prerequisite.parentId) || props.existingOperators.length === 0) return false;

  const operators: Record<number, string> = {};
  props.existingOperators.forEach((op) => {
    if (notDefined(operators[op.ruleConditionGroupId])) {
      operators[op.ruleConditionGroupId] = op.operator;
    }
  });

  const nextIndex = index + 1;
  if (nextIndex < props.existingPrerequisites.length) {
    const currentParentId = prerequisite.parentId as number;
    const currentOperator = operators[currentParentId];
    const nextPrerequisite = props.existingPrerequisites[nextIndex];
    return (
      currentOperator === PREREQUISITE_OPERATOR.OR || currentParentId !== nextPrerequisite.parentId
    );
  }
  return false;
};

const titleLabel = (prerequisite: PrerequisiteItem) => {
  if (
    isQuizScore(prerequisite.criterion) ||
    isQuizMarkAward(prerequisite.criterion) ||
    isQuizRank(prerequisite.criterion)
  ) {
    return prerequisite.activityTitle;
  } else if (isSectionOrActivityStarts(prerequisite.criterion)) {
    if (prerequisite.resourceCompletionPrerequisite === 0)
      return t("gamification_condition_lesson_starts_label") as string;
    return isNull(prerequisite.activityPageNo)
      ? prerequisite.sectionTitle
      : prerequisite.activityTitle;
  } else if (isSectionOrActivityEnds(prerequisite.criterion)) {
    return isNull(prerequisite.activityPageNo)
      ? prerequisite.sectionTitle
      : prerequisite.activityTitle;
  } else if (isLevelUp(prerequisite.criterion)) {
    return t("gamification_condition_level_label", {
      level: prerequisite.levelPrerequisite,
    }) as string;
  } else if (isGameAward(prerequisite.criterion)) {
    return prerequisite.gameAwardTitle;
  } else if (isSequentialCompletionPrerequisite(prerequisite.criterion)) {
    return isNull(prerequisite.activityPageNo)
      ? prerequisite.sectionTitle
      : prerequisite.activityTitle;
  } else if (isGameChoice(prerequisite.criterion)) {
    return prerequisite.choiceGameAwardTitle;
  }
  return "";
};

const criterionText = (prerequisite: PrerequisiteItem) => {
  if (isQuizScore(prerequisite.criterion) || isQuizMarkAward(prerequisite.criterion)) {
    return t("prerequisite_content_popup_quiz_completion_criterion_text", {
      quizScoreFrom: prerequisite.quizScoreFrom,
      quizScoreTo: prerequisite.quizScoreTo,
    }) as string;
  } else if (isSectionOrActivityStarts(prerequisite.criterion)) {
    if (prerequisite.resourceCompletionPrerequisite !== 0)
      return t("gamification_condition_starts_label") as string;
  } else if (isSectionOrActivityEnds(prerequisite.criterion)) {
    return t("gamification_condition_completed_label") as string;
  } else if (isLevelUp(prerequisite.criterion)) {
    return t("gamification_condition_level_message") as string;
  } else if (isGameAward(prerequisite.criterion)) {
    return t("gamification_condition_game_award_label") as string;
  } else if (isSequentialCompletionPrerequisite(prerequisite.criterion)) {
    return t("gamification_condition_completed_label") as string;
  } else if (isQuizRank(prerequisite.criterion)) {
    return t("gamification_condition_quiz_rank_label", {
      minRankPosition: prerequisite.minRankPosition,
      maxRankPosition: prerequisite.maxRankPosition,
    }) as string;
  } else if (isGameChoice(prerequisite.criterion)) {
    if (props.allOtherResponseBranchIdList.includes(prerequisite.gameChoice as number)) {
      return t("gamification_all_other_responses_label") as string;
    }
    return prerequisite.choices ?? "";
  }
  return "";
};

const showSectionIcon = (prerequisite: PrerequisiteItem) =>
  !isNull(prerequisite.sectionPageNo) && isConditionRelatedToActivityOrQuiz(prerequisite.criterion);

const showActivityTitle = (prerequisite: PrerequisiteItem) => !isNull(prerequisite.activityPageNo);

const showIconOtherThanSectionIcon = (prerequisite: PrerequisiteItem) =>
  isNull(prerequisite.sectionPageNo) && !isGameChoice(prerequisite.criterion);

const isGameChoiceType = (prerequisite: PrerequisiteItem) => isGameChoice(prerequisite.criterion);

const userGameStories = computed(() => props.userGameData?.userGameStories ?? []);
const userAchievements = computed(() => props.userGameData?.achievements ?? []);
const userAvatars = computed(() => props.userGameData?.userAwardedGameAvatars ?? []);
const userDigitalBadges = computed(() => props.userGameData?.userAwardedDigitalBadges ?? []);

const chosenChoiceHistory = (gameChoiceId?: number) => {
  const choice = (gameChoiceId ? props.choiceListById?.[gameChoiceId] : undefined) ?? {};
  return choice.answerText ? choice.answerText : (choice.choices ?? "");
};

const chosenChoiceHistoryIcon = (gameChoiceId?: number) => {
  const choice = (gameChoiceId ? props.choiceListById?.[gameChoiceId] : undefined) ?? {};
  return choice.acceptableResponse ? "CorrectBranch32" : "WrongBranch32";
};

const choiceIcon = (prerequisite: PrerequisiteItem) => {
  const choiceGameAwardId = parseInt(String(prerequisite.choiceGameAwardId));
  const gameAward = props.gameAwards.find((ga) => ga.id === choiceGameAwardId);
  const isSelectChoiceType = String(gameAward?.choiceType) === GAME_CHOICE_TYPE.SELECT;
  if (isSelectChoiceType) return "Choice32";
  const gc = gameAward?.gameChoiceList?.find((g) => g.id === prerequisite.gameChoice);
  return gc?.acceptableResponse ? "CorrectBranch32" : "WrongBranch32";
};

const defaultIcons = computed(() => ({
  intro: "Introduction32",
  section: "Section32",
  activity: "Page32",
  quiz: "QuizPage32",
}));

onMounted(() => {
  nextTick(() => {
    visible.value = true;
  });
});

onBeforeUnmount(() => {
  if (visible.value) handleClose();
});

function hasAccess(prerequisite: PrerequisiteItem) {
  if (!isStudent.value) return true;

  if (isGameStory(prerequisite)) {
    return userGameStories.value.some((gs) => gs.id === prerequisite.gameAwardPrerequisite);
  }
  if (isGameAchievement(prerequisite)) {
    return userAchievements.value.some((ach) => ach.id === prerequisite.gameAwardPrerequisite);
  }
  if (isGameAvatar(prerequisite)) {
    return userAvatars.value.some((av) => av.id === prerequisite.gameAwardPrerequisite);
  }
  if (isDigitalBadge(prerequisite)) {
    return userDigitalBadges.value.some((db) => db.id === prerequisite.gameAwardPrerequisite);
  }
  if (isGameChoice(prerequisite.criterion)) {
    const userGameAwards = [
      ...userGameStories.value,
      ...userAchievements.value,
      ...userAvatars.value,
      ...userDigitalBadges.value,
    ];
    prerequisite.gameAwardPrerequisite = parseInt(String(prerequisite.choiceGameAwardId));
    return userGameAwards.some((aw) => aw.id === prerequisite.gameAwardPrerequisite);
  }
  return true;
}

function handleClose() {
  if (
    props.isGameStoryCondition ||
    props.isAchievementCondition ||
    props.isGameAvatarCondition ||
    props.isDigitalBadgeCondition
  ) {
    emit("close");
  } else {
    emit("event", {
      type: EVENT.TOGGLE_VIEW_PREREQUISITE_CONTENT_POPUP,
      data: {
        id: props.isSectionPrerequisite ? props.currentSectionId : props.currentActivityId,
        visible: false,
      },
    });
  }
  visible.value = false;
}

function gameAwardTargetIdForRedirect(prerequisite: PrerequisiteItem) {
  if (isGameChoice(prerequisite.criterion)) {
    return prerequisite.choiceGameAwardId;
  }
  return prerequisite.gameAwardPrerequisite ? prerequisite.gameAwardPrerequisite : undefined;
}

function handleOpenResource(prerequisite: PrerequisiteItem) {
  if (
    props.isGameStoryCondition ||
    props.isAchievementCondition ||
    props.isGameAvatarCondition ||
    props.isDigitalBadgeCondition
  ) {
    emit("gameEvent", {
      data: {
        type: prerequisite.criterion,
        sectionPageNo: prerequisite.sectionPageNo ?? undefined,
        activityPageNo: prerequisite.activityPageNo ?? undefined,
        sectionId: prerequisite.sectionId ?? undefined,
        activityId: prerequisite.activityId ?? undefined,
        gameAwardType: prerequisite.gameAwardType ?? undefined,
        gameAwardTargetId: gameAwardTargetIdForRedirect(prerequisite),
      },
    });
  } else {
    if (!isNull(prerequisite.activityId)) {
      emit("event", {
        type: EVENT.ACTIVITY_NAVIGATION,
        data: { id: prerequisite.activityId as number },
      });
    } else {
      emit("event", {
        type: EVENT.SECTION_COVER_NAVIGATION,
        data: { id: prerequisite.sectionId as number },
      });
    }
  }
}

function iconName(prerequisite: PrerequisiteItem) {
  if (
    isSectionOrActivityStarts(prerequisite.criterion) ||
    isSectionOrActivityEnds(prerequisite.criterion)
  ) {
    return getActivityIcon(prerequisite);
  }
  if (isQuizMarkAward(prerequisite.criterion) || isQuizRank(prerequisite.criterion)) {
    return getActivityIcon(prerequisite);
  }
  if (isGameAwardOrGameChoiceStory(prerequisite)) return "GameStory32";
  if (isGameAwardOrGameChoiceAchievement(prerequisite)) return "Achievement32";
  if (isGameAwardOrGameChoiceAvatar(prerequisite)) return "GameAvatar32";
  if (isGameAwardOrGameChoiceBadge(prerequisite)) return "DigitalBadge32";
  if (isLevelUp(prerequisite.criterion)) return "Level32";
  if (isQuizScore(prerequisite.criterion)) return getActivityIcon(prerequisite);
  if (
    isSequentialCompletionPrerequisite(prerequisite.criterion) &&
    !isNull(prerequisite.activityPageNo)
  )
    return getActivityIcon(prerequisite);
  return "";
}

function getSectionIcon(prerequisite: PrerequisiteItem) {
  if (prerequisite.sectionIconName) return prerequisite.sectionIconName;
  return defaultIcons.value.section;
}

function getActivityIcon(prerequisite: PrerequisiteItem) {
  if (prerequisite.resourceCompletionPrerequisite === 0) {
    if (prerequisite.introIconName) return prerequisite.introIconName;
    return defaultIcons.value.intro;
  }
  if (!prerequisite.activityIconName) {
    return prerequisite.activityType === LESSON_ACTIVITY_TYPE.ACTIVITY
      ? defaultIcons.value.activity
      : defaultIcons.value.quiz;
  }
  return prerequisite.activityIconName;
}

function isConditionRelatedToActivityOrQuiz(criterion: string) {
  return (
    isSectionOrActivityEndsOrStarts(criterion) ||
    isQuizMarkAward(criterion) ||
    isQuizScore(criterion) ||
    isQuizRank(criterion)
  );
}

function isConditionRelatedToGame(criterion: string) {
  return (
    isGameAward(criterion) ||
    isGameChoice(criterion) ||
    isLevelUp(criterion) ||
    isQuizRank(criterion)
  );
}

function isSequentialCompletionPrerequisite(criterion: string) {
  return criterion === RULE_CONDITION_TYPE.PREVIOUS_COMPLETION;
}

function isQuizScore(criterion: string) {
  return criterion === RULE_CONDITION_TYPE.QUIZ_SCORE;
}

function isSectionOrActivityStarts(criterion: string) {
  return criterion === CONDITION_TYPE.RESOURCE_STARTS;
}

function isSectionOrActivityEnds(criterion: string) {
  return criterion === CONDITION_TYPE.RESOURCE_COMPLETE;
}

function isQuizMarkAward(criterion: string) {
  return criterion === CONDITION_TYPE.QUIZ_MARK_AWARD;
}

function isLevelUp(criterion: string) {
  return criterion === CONDITION_TYPE.LEVEL_UP;
}

function isGameAward(criterion: string) {
  return criterion === CONDITION_TYPE.GAME_AWARD;
}

function isQuizRank(criterion: string) {
  return criterion === CONDITION_TYPE.QUIZ_RANK;
}

function isGameAchievement(condition: PrerequisiteItem) {
  return (
    condition.criterion === CONDITION_TYPE.GAME_AWARD &&
    String(condition?.gameAwardType) === GAME_AWARD_TYPE.ACHIEVEMENT
  );
}

function isGameAwardOrGameChoiceAchievement(condition: PrerequisiteItem) {
  return (
    (condition.criterion === CONDITION_TYPE.GAME_AWARD ||
      condition.criterion === CONDITION_TYPE.GAME_CHOICE) &&
    String(condition?.gameAwardType) === GAME_AWARD_TYPE.ACHIEVEMENT
  );
}

function isGameAwardOrGameChoiceAvatar(condition: PrerequisiteItem) {
  return (
    (condition.criterion === CONDITION_TYPE.GAME_AWARD ||
      condition.criterion === CONDITION_TYPE.GAME_CHOICE) &&
    condition.gameAwardType === GAME_AWARD_TYPE.GAME_AVATAR
  );
}

function isGameAwardOrGameChoiceStory(condition: PrerequisiteItem) {
  return (
    (condition.criterion === CONDITION_TYPE.GAME_AWARD ||
      condition.criterion === CONDITION_TYPE.GAME_CHOICE) &&
    condition.gameAwardType === GAME_AWARD_TYPE.GAME_STORY
  );
}

function isGameAwardOrGameChoiceBadge(condition: PrerequisiteItem) {
  return (
    (condition.criterion === CONDITION_TYPE.GAME_AWARD ||
      condition.criterion === CONDITION_TYPE.GAME_CHOICE) &&
    condition.gameAwardType === GAME_AWARD_TYPE.DIGITAL_BADGE
  );
}

function isGameAvatar(condition: PrerequisiteItem) {
  return (
    condition.criterion === CONDITION_TYPE.GAME_AWARD &&
    condition.gameAwardType === GAME_AWARD_TYPE.GAME_AVATAR
  );
}

function isGameStory(condition: PrerequisiteItem) {
  return (
    condition.criterion === CONDITION_TYPE.GAME_AWARD &&
    condition.gameAwardType === GAME_AWARD_TYPE.GAME_STORY
  );
}

function isDigitalBadge(condition: PrerequisiteItem) {
  return (
    condition.criterion === CONDITION_TYPE.GAME_AWARD &&
    condition.gameAwardType === GAME_AWARD_TYPE.DIGITAL_BADGE
  );
}

function isGameCondition(conditionType: string) {
  return conditionType === CONDITION_TYPE.GAME_AWARD || conditionType === CONDITION_TYPE.LEVEL_UP;
}

function isGameChoice(conditionType: string) {
  return conditionType === CONDITION_TYPE.GAME_CHOICE;
}

function isSectionOrActivityEndsOrStarts(criterion: string) {
  return (
    isSectionOrActivityStarts(criterion) ||
    isSectionOrActivityEnds(criterion) ||
    isSequentialCompletionPrerequisite(criterion)
  );
}

function isGameConditionAndGameDisabled(criterion: string) {
  return isConditionRelatedToGame(criterion) && !props.isGameEnabled;
}
</script>
